// ===== frontend/src/hooks/useFormData.js (Final version with localStorage) =====
import { useState, useEffect } from 'react';
import { updateFormFields } from '../utils/api';

export const useFormData = (sessionId) => {
  const [formData, setFormData] = useState({});
  const [formStructure, setFormStructure] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load form structure and data from localStorage
  useEffect(() => {
    if (sessionId) {
      loadFormData(sessionId);
    }
  }, [sessionId]);

  const loadFormData = async (id) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Get form structure from localStorage
      const storedStructure = localStorage.getItem(`form_structure_${id}`);
      const storedData = localStorage.getItem(`form_data_${id}`);
      
      if (storedStructure) {
        const structure = JSON.parse(storedStructure);
        setFormStructure(structure);
        setFormData(JSON.parse(storedData || '{}'));
      } else {
        throw new Error('Formulardaten nicht gefunden. Bitte laden Sie das PDF erneut hoch.');
      }
    } catch (err) {
      console.error('Error loading form data:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Update field value
  const updateField = (fieldId, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [fieldId]: value
      };
      
      // Save to localStorage immediately
      localStorage.setItem(`form_data_${sessionId}`, JSON.stringify(newData));
      
      return newData;
    });
    setHasUnsavedChanges(true);
  };

  // Update multiple fields
  const updateFields = (fields) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        ...fields
      };
      
      // Save to localStorage immediately
      localStorage.setItem(`form_data_${sessionId}`, JSON.stringify(newData));
      
      return newData;
    });
    setHasUnsavedChanges(true);
  };

  // Save to backend
  const saveFormData = async () => {
    if (!sessionId || !hasUnsavedChanges) return true;

    try {
      await updateFormFields(sessionId, formData);
      setHasUnsavedChanges(false);
      return true;
    } catch (err) {
      console.error('Error saving to backend:', err);
      // Still return true since we have localStorage backup
      setHasUnsavedChanges(false);
      return true;
    }
  };

  // Auto-save effect
  useEffect(() => {
    if (hasUnsavedChanges) {
      const timer = setTimeout(saveFormData, 2000); // Auto-save after 2 seconds
      return () => clearTimeout(timer);
    }
  }, [formData, hasUnsavedChanges, sessionId]);

  return {
    formData,
    formStructure,
    isLoading,
    error,
    hasUnsavedChanges,
    updateField,
    updateFields,
    saveFormData,
    reload: () => loadFormData(sessionId)
  };
};